// src/components/Toast.jsx
// Short-lived messages. Without a provider (e.g. in isolated tests) the functions do nothing.
import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

const noop = () => {};
const ToastContext = createContext({ success: noop, error: noop, info: noop });

const DURATION_MS = { success: 3500, info: 3500, error: 6500 };

export function ToastProvider({ children }) {
    const [toasts, setToasts] = useState([]);
    const nextId = useRef(0);

    const push = useCallback((type, message) => {
        nextId.current += 1;
        const id = nextId.current;
        setToasts((current) => [...current, { id, type, message }]);
        setTimeout(() => setToasts((current) => current.filter((toast) => toast.id !== id)), DURATION_MS[type]);
    }, []);

    const api = useMemo(
        () => ({
            success: (message) => push('success', message),
            error: (message) => push('error', message),
            info: (message) => push('info', message),
        }),
        [push]
    );

    return (
        <ToastContext.Provider value={api}>
            {children}
            <div className="toasts" aria-live="polite">
                {toasts.map((toast) => (
                    <div key={toast.id} className={`toast toast-${toast.type}`} role={toast.type === 'error' ? 'alert' : 'status'}>
                        {toast.message}
                    </div>
                ))}
            </div>
        </ToastContext.Provider>
    );
}

export const useToast = () => useContext(ToastContext);
